import React from "react";
import { isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { BsArrowUpShort, BsTrash } from "react-icons/bs";
import TableHeaderCell from "components/reusable/TableHeaderCell";

const Table = ({ earningManager, page, sort, setSort, manager, onDelete }) => {
  const { t } = useTranslation();

  const handleDelete = (transactionId) => {
    if (window.confirm(t("CONFIRM_DELETE_TRANSACTION"))) {
      onDelete(transactionId);
    }
  };

  // Helper: detect if this is a background check transaction
  const isBgCheck = (item) => !!item?.metadata?.checkTypeDetails?.length;

  // Helper: resolve company id/name for both record types
  const getCompanyId = (item) =>
    item?.Company?._id || item?.metadata?.companyID || "N/A";

  const getCompanyName = (item) => {
    if (item?.Company?.name) return item.Company.name;
    // Background check payload only has employeeData, not a company name
    const { firstName, lastName } = item?.metadata?.employeeData || {};
    if (firstName || lastName) return `${firstName || ""} ${lastName || ""}`.trim();
    return "N/A";
  };

  const getSubscriptionId = (item) =>
    item?.Subscription?._id || item?.subscriptionID || "N/A";

  // UPDATED FUNCTION
  const getSubscriptionName = (item) => {
    // 1. Handle Payroll Setup explicitly
    if (item?.transactionType === "payroll_setup") {
      return "Payroll Setup";
    }

    // 2. Handle Background Checks
    if (isBgCheck(item)) {
      return item?.metadata?.checkTypeDetails
        ?.map((c) => c.name)
        .join(", ") || "Background Check";
    }

    // 3. Handle standard Subscriptions and Upgrades
    const base = item?.Subscription?.name || "N/A";
    const type = item?.metadata?.type;
    
    if (type === "employee_upgrade") return `${base} - Employee Upgrade`;
    if (type === "renewal") return `${base} - Renewal`;
    
    // 4. Fallback: If base is still "N/A" but we have a transactionType, simplify it
    if (base === "N/A" && item?.transactionType) {
      // Converts "some_transaction_type" to "Some Transaction Type"
      return item.transactionType
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }

    return base;
  };

  const getAmount = (item) => {
    if (isBgCheck(item)) {
      return (
        item?.metadata?.checkTypeDetails?.reduce(
          (sum, c) => sum + (c.price || 0),
          0
        ) ?? "N/A"
      );
    }
    if (item?.metadata?.type === "employee_upgrade") {
      return item?.metadata?.additionalAmount;
    }
    return (
      item?.metadata?.newAmount ??
      item?.Subscription?.amount ??
      "N/A"
    );
  };

  return (
    <div className="p-3">
      <div className="overflow-x-auto relative rounded-lg border">
        <table className="w-full text-xs text-left text-[#6c6c6c] dark:text-gray-400">
          <thead className="text-xs text-gray-900 border border-[#E1E6EE] bg-[#E1E6EE] dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <TableHeaderCell text={t("SR_NO")} />
              <TableHeaderCell text={t("COMPANY_ID")} />
              <TableHeaderCell text={t("Company")} />
              {/* <TableHeaderCell text={t("SUBSCRIPTION_ID")} /> */}
              <TableHeaderCell text={t("Title")} />
              <TableHeaderCell text={t("Amount")} />
              <th
                scope="col"
                className="py-3 px-6 cursor-pointer"
                onClick={() => {
                  setSort({
                    sortBy: "createdAt",
                    sortType:
                      sort.sortBy === "createdAt" && sort.sortType === "asc"
                        ? "desc"
                        : "asc",
                  });
                }}
              >
                <div className="flex justify-start">
                  <span>{t("PURCHASED_DATE")}</span>
                  <span>
                    {sort.sortBy === "createdAt" &&
                      sort.sortType === "asc" && (
                        <BsArrowUpShort className="w-4 h-4" />
                      )}
                    {sort.sortBy === "createdAt" &&
                      sort.sortType === "desc" && (
                        <BsArrowUpShort className="w-4 h-4 rotate-180" />
                      )}
                  </span>
                </div>
              </th>
              <TableHeaderCell text={t("TRANSACTIONS_ID")} />
              <TableHeaderCell text={t("ACTION")} />
            </tr>
          </thead>
          <tbody>
            {earningManager?.map((item, i) => (
              <tr
                key={item._id || i}
                className="bg-white border-b dark:bg-gray-800 dark:border-gray-700"
              >
                <th
                  scope="row"
                  className="py-4 px-6 border-r font-medium text-gray-900 dark:text-white"
                >
                  {i + 1 + 10 * (page - 1)}
                </th>
                <td className="py-4 px-6 border-r text-gray-900">
                  {getCompanyId(item)}
                </td>
                <td className="py-4 px-6 border-r text-gray-900">
                  {getCompanyName(item)}
                </td>
                {/* <td className="py-4 px-6 border-r text-gray-900">
                  {getSubscriptionId(item)}
                </td> */}
                <td className="py-4 px-6 border-r text-gray-900">
                  {getSubscriptionName(item)}
                </td>
                <td className="py-4 px-6 border-r text-gray-900">
                  {getAmount(item)}
                </td>
                <td className="py-4 px-6 border-r text-gray-900">
                  {dayjs(item?.createdAt).format("MM-DD-YYYY hh:mm A")}
                </td>
                <td className="py-4 px-6 border-r text-gray-900">
                  {item?.transactionId || "N/A"}
                </td>
                <td className="py-4 px-6 border-r text-gray-900">
                  <button
                    onClick={() => handleDelete(item?.transactionId)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <BsTrash />
                  </button>
                </td>
              </tr>
            ))}
            {isEmpty(earningManager) && (
              <tr className="bg-white border-b text-center dark:bg-gray-800 dark:border-gray-700">
                <td className="py-4 px-6 border-r" colSpan={10}>
                  {t("O_NO_RECORD_FOUND")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Table;





// import React from "react";
// import { isEmpty } from "lodash";
// import { useTranslation } from "react-i18next";
// import dayjs from "dayjs";
// import { BsArrowUpShort, BsTrash } from "react-icons/bs";
// import TableHeaderCell from "components/reusable/TableHeaderCell";

// const Table = ({ earningManager, page, sort, setSort, manager, onDelete }) => {
//   const { t } = useTranslation();

//   const handleDelete = (transactionId) => {
//     if (window.confirm(t("CONFIRM_DELETE_TRANSACTION"))) {
//       onDelete(transactionId);
//     }
//   };

//   // Helper: detect if this is a background check transaction
//   const isBgCheck = (item) => !!item?.metadata?.checkTypeDetails?.length;

//   // Helper: resolve company id/name for both record types
//   const getCompanyId = (item) =>
//     item?.Company?._id || item?.metadata?.companyID || "N/A";

//   const getCompanyName = (item) => {
//     if (item?.Company?.name) return item.Company.name;
//     // Background check payload only has employeeData, not a company name
//     const { firstName, lastName } = item?.metadata?.employeeData || {};
//     if (firstName || lastName) return `${firstName || ""} ${lastName || ""}`.trim();
//     return "N/A";
//   };

//   const getSubscriptionId = (item) =>
//     item?.Subscription?._id || item?.subscriptionID || "N/A";

//   const getSubscriptionName = (item) => {
//     if (isBgCheck(item)) {
//       return item?.metadata?.checkTypeDetails
//         ?.map((c) => c.name)
//         .join(", ") || "Background Check";
//     }
//     const base = item?.Subscription?.name || "N/A";
//     const type = item?.metadata?.type;
//     if (type === "employee_upgrade") return `${base} - Employee Upgrade`;
//     if (type === "renewal") return `${base} - Renewal`;
//     return base;
//   };

//   const getAmount = (item) => {
//     if (isBgCheck(item)) {
//       return (
//         item?.metadata?.checkTypeDetails?.reduce(
//           (sum, c) => sum + (c.price || 0),
//           0
//         ) ?? "N/A"
//       );
//     }
//     if (item?.metadata?.type === "employee_upgrade") {
//       return item?.metadata?.additionalAmount;
//     }
//     return (
//       item?.metadata?.newAmount ??
//       item?.Subscription?.amount ??
//       "N/A"
//     );
//   };

//   return (
//     <div className="p-3">
//       <div className="overflow-x-auto relative rounded-lg border">
//         <table className="w-full text-xs text-left text-[#6c6c6c] dark:text-gray-400">
//           <thead className="text-xs text-gray-900 border border-[#E1E6EE] bg-[#E1E6EE] dark:bg-gray-700 dark:text-gray-400">
//             <tr>
//               <TableHeaderCell text={t("SR_NO")} />
//               <TableHeaderCell text={t("COMPANY_ID")} />
//               <TableHeaderCell text={t("Company")} />
//               {/* <TableHeaderCell text={t("SUBSCRIPTION_ID")} /> */}
//               <TableHeaderCell text={t("Title")} />
//               <TableHeaderCell text={t("Amount")} />
//               <th
//                 scope="col"
//                 className="py-3 px-6 cursor-pointer"
//                 onClick={() => {
//                   setSort({
//                     sortBy: "createdAt",
//                     sortType:
//                       sort.sortBy === "createdAt" && sort.sortType === "asc"
//                         ? "desc"
//                         : "asc",
//                   });
//                 }}
//               >
//                 <div className="flex justify-start">
//                   <span>{t("PURCHASED_DATE")}</span>
//                   <span>
//                     {sort.sortBy === "createdAt" &&
//                       sort.sortType === "asc" && (
//                         <BsArrowUpShort className="w-4 h-4" />
//                       )}
//                     {sort.sortBy === "createdAt" &&
//                       sort.sortType === "desc" && (
//                         <BsArrowUpShort className="w-4 h-4 rotate-180" />
//                       )}
//                   </span>
//                 </div>
//               </th>
//               <TableHeaderCell text={t("TRANSACTIONS_ID")} />
//               <TableHeaderCell text={t("ACTION")} />
//             </tr>
//           </thead>
//           <tbody>
//             {earningManager?.map((item, i) => (
//               <tr
//                 key={item._id || i}
//                 className="bg-white border-b dark:bg-gray-800 dark:border-gray-700"
//               >
//                 <th
//                   scope="row"
//                   className="py-4 px-6 border-r font-medium text-gray-900 dark:text-white"
//                 >
//                   {i + 1 + 10 * (page - 1)}
//                 </th>
//                 <td className="py-4 px-6 border-r text-gray-900">
//                   {getCompanyId(item)}
//                 </td>
//                 <td className="py-4 px-6 border-r text-gray-900">
//                   {getCompanyName(item)}
//                 </td>
//                 {/* <td className="py-4 px-6 border-r text-gray-900">
//                   {getSubscriptionId(item)}
//                 </td> */}
//                 <td className="py-4 px-6 border-r text-gray-900">
//                   {getSubscriptionName(item)}
//                 </td>
//                 <td className="py-4 px-6 border-r text-gray-900">
//                   {getAmount(item)}
//                 </td>
//                 <td className="py-4 px-6 border-r text-gray-900">
//                   {dayjs(item?.createdAt).format("MM-DD-YYYY hh:mm A")}
//                 </td>
//                 <td className="py-4 px-6 border-r text-gray-900">
//                   {item?.transactionId || "N/A"}
//                 </td>
//                 <td className="py-4 px-6 border-r text-gray-900">
//                   <button
//                     onClick={() => handleDelete(item?.transactionId)}
//                     className="text-red-500 hover:text-red-700"
//                   >
//                     <BsTrash />
//                   </button>
//                 </td>
//               </tr>
//             ))}
//             {isEmpty(earningManager) && (
//               <tr className="bg-white border-b text-center dark:bg-gray-800 dark:border-gray-700">
//                 <td className="py-4 px-6 border-r" colSpan={10}>
//                   {t("O_NO_RECORD_FOUND")}
//                 </td>
//               </tr>
//             )}
//           </tbody>
//         </table>
//       </div>
//     </div>
//   );
// };

// export default Table;